import { makeAdvancedReportsApi, makeBenefitsApi } from 'src/http/api/internal/internal-http';
import {
  BenefitBulkEditProgress,
  BenefitBulkEditFeedback,
  BenefitBulkEditSession,
  BenefitsRepository,
} from 'src/repositories/benefits/benefits.repository';
import { BenefitBulkUpdateInput, Benefits } from 'src/types/benefits';
import { create } from 'zustand';

type States = {
  isLoadingBenefitsList: boolean;
  benefitsList: {
    data: Benefits[];
    page: number;
    pageSize: number;
    total: number;
    totalFiltered: number;
    totalPages: number;
  } | null;
  userRedemptions: any[];
};

type Actions = {
  getBenefitsList: (
    searchText: string,
    filters: Array<{ field?: string; column?: string; value: unknown }>,
    sortModel: any,
    paginationModel: any
  ) => Promise<void>;
  getUserRedemptions: (userId: string) => Promise<void>;
  exportData: (
    benefits: Array<{ id: string; type: 'coupon' | 'product' }>,
    userName: string,
    options?: { isAllSelected?: boolean }
  ) => Promise<void>;
  changeBenefitStatus: (benefitId: string, newStatus: 'active' | 'inactive') => Promise<void>;
  createBulkEditSession: (
    clientIdempotencyKey: string,
    totalItems: number
  ) => Promise<BenefitBulkEditSession>;
  submitBulkEditBatch: (
    bulkSessionKey: string,
    batchKey: string,
    batchIndex: number,
    benefits: BenefitBulkUpdateInput[]
  ) => Promise<void>;
  getBulkEditProgress: (
    bulkSessionKey: string,
    updatedAfter?: string
  ) => Promise<BenefitBulkEditProgress>;
  getBulkEditFeedback: (bulkSessionKey: string) => Promise<BenefitBulkEditFeedback>;
};

type Store = Actions & States;

const initialValues = {
  isLoadingBenefitsList: false,
  benefitsList: null,
  userRedemptions: [],
};

const repository = new BenefitsRepository(makeBenefitsApi());
const advancedReportsRepository = new BenefitsRepository(makeAdvancedReportsApi());

export const useBenefitsStore = create<Store>((set) => ({
  ...initialValues,

  getBenefitsList: async (searchText, filters, sortModel, paginationModel) => {
    set({ isLoadingBenefitsList: true });

    try {
      const params = {
        search: searchText,
        filters: JSON.stringify(filters),
        sort: JSON.stringify(sortModel),
        page: paginationModel.page,
        pageSize: paginationModel.pageSize,
      };

      const response = await repository.getBenefitsList(params);

      if (response.isLeft()) {
        set({ isLoadingBenefitsList: false });
        return;
      }

      set({ benefitsList: response.value, isLoadingBenefitsList: false });
    } catch {
      set({ isLoadingBenefitsList: false });
    }
  },

  getUserRedemptions: async (userId: string) => {
    const response = await repository.getUserRedemptions({ userId });

    if (response.isLeft()) {
      throw new Error(response.value.message);
    }
    set({ userRedemptions: response.value || [] });
  },

  exportData: async (benefits, userName, options) => {
    await advancedReportsRepository.exportBenefits(benefits, userName, options);
  },

  changeBenefitStatus: async (benefitId: string, newStatus: 'active' | 'inactive') => {
    const response = await repository.changeBenefitStatus({ benefitId, newStatus });
    if (response.isLeft()) {
      throw new Error(response.value.message);
    }
  },

  createBulkEditSession: async (clientIdempotencyKey, totalItems) => {
    const response = await repository.createBulkEditSession(clientIdempotencyKey, totalItems);
    if (response.isLeft() || !response.value) {
      throw new Error(response.isLeft() ? response.value.message : 'Não foi possível iniciar a edição em massa.');
    }
    return response.value;
  },

  submitBulkEditBatch: async (bulkSessionKey, batchKey, batchIndex, benefits) => {
    const response = await repository.submitBulkEditBatch(bulkSessionKey, batchKey, batchIndex, benefits);
    if (response.isLeft()) {
      throw new Error(response.value.message);
    }
  },

  getBulkEditProgress: async (bulkSessionKey, updatedAfter) => {
    const response = await repository.getBulkEditProgress(bulkSessionKey, updatedAfter);
    if (response.isLeft() || !response.value) {
      throw new Error(response.isLeft() ? response.value.message : 'Não foi possível consultar o progresso da edição.');
    }
    return response.value;
  },

  getBulkEditFeedback: async (bulkSessionKey) => {
    const response = await repository.getBulkEditFeedback(bulkSessionKey);
    if (response.isLeft() || !response.value) {
      throw new Error(response.isLeft() ? response.value.message : 'Não foi possível consultar os erros da edição.');
    }
    return response.value;
  },
}));
